import { EventEmitter, Injectable } from '@angular/core';
import { Subject, Observable, of, firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UnsavedChangesModalService {
  private decisionSubject = new Subject<boolean>();
  public showModalEmitter = new EventEmitter<boolean>();
  private isInEditMode = false;
  private decisionResultSubject = new Subject<boolean>();


  constructor() {
    this.showModalEmitter.emit(this.isInEditMode);
  }

  // GENERAL FLOW /////////////////////////////////////////////////////////////////////////////////
  //
  // 1) set the edit mode value according to your purpose using 'setIsInEditMode'
  // 2) add canDeactivate: [UnsavedChangesGuard] to selected routing module
  //   2.1) if needed add 'checkCanProceed' to hor-nav-bar
  // 3) add function to your component, where you check editState and show modal
  //    canDeactivate(): Observable<boolean> | boolean {
  //      if (this.unsavedChangesModalService.getIsInEditMode()) {
  //        return this.unsavedChangesModalService.showConfirmDialog();
  //      } else {
  //        return true;
  //      }
  //    }
  // 3.1) if needed use 'getDecisionResult' in your component, to make some logic after tab changing
  // 4) be happy!
  //
  // ////////////////////////////////////////////////////////////////////////////////////////////////


  /**
   * Triggers show modal if user is in edit mode and returns an Observable
   * that emits the user's decision (true -> proceed with unsaved changes, false -> stay in the same page).
   * If not in edit mode, immediately returns an Observable of false, indicating no need for confirmation.
   *
   * @returns An Observable that emits the user's decision.
   */
  showConfirmDialog(): Observable<boolean> {
    if (this.isInEditMode) {
      this.showModalEmitter.emit(this.isInEditMode);
      return this.decisionSubject.asObservable();
    }
    return of(false);
  }

  /**
   * Sets the edit mode state of selected page (component).
   * If set to true -> user has unsaved changes.
   *
   * @param isEditing - The new edit mode state.
   */
  setIsInEditMode(isEditing: boolean) {
    this.isInEditMode = isEditing;
  }

  /**
   * Get the current edit mode state.
   *
   * @returns True -> is in edit mode, false otherwise.
   */
  getIsInEditMode(): boolean {
    return this.isInEditMode;
  }

  /**
   * Resolves the modal based on the user's decision. Completes the current decision.
   *
   * @param decision - The user's decision (true -> proceed, false -> cancel modal and stay).
   */
  resolveDialog(decision: boolean) {
    this.decisionSubject.next(decision);
    this.decisionSubject.complete();
    this.decisionSubject = new Subject<boolean>();
    if (decision) {
      this.isInEditMode = false;
    }
    this.decisionResultSubject.next(decision);
    this.showModalEmitter.emit(false);
  }


  /**
   * Get an Observable that emits the result of the user's decision from the modal.
   */
  getDecisionResult(): Observable<boolean> {
    return this.decisionResultSubject.asObservable();
  }

  /**
   * Checks if the function can proceed with an action, based on the edit mode state in the page (component).
   * If in edit mode, displays the confirmation dialog and waits for the user's decision.
   * Returns true if user wants to proceed, false if the action should be cancelled.
   *
   * @returns A promise that resolves with the user's decision.
   */
  async checkCanProceed(): Promise<boolean> {
    if (this.getIsInEditMode()) {
      const canProceed = await firstValueFrom(this.showConfirmDialog());
      return canProceed;
    }
    return true;
  }
}
